import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';
import { draftSliceForStep, mapStepErrors, sessionToClientState } from './onboardingMappers';

export { draftSliceForStep, mapStepErrors, sessionToClientState, normalizeDraft } from './onboardingMappers';

function unwrapPost(res) {
  const body = res?.data;
  if (res.status >= 200 && res.status < 300 && body?.status === true) {
    return { ok: true, data: body.data, status: res.status };
  }
  return { ok: false, data: body?.data ?? body, status: res.status };
}

async function postWizard(path, body = {}) {
  try {
    const res = await APIService.post(path, body);
    return unwrapPost(res);
  } catch (err) {
    const status = err.response?.status ?? 0;
    const respBody = err.response?.data;
    if (respBody && typeof respBody === 'object' && 'data' in respBody) {
      return { ok: false, data: respBody.data, status };
    }
    throw err;
  }
}

export async function fetchCampaignFormTemplates(businessTypeId, goals) {
  const goalsParam = goals.join(',');
  const res = await APIService.get(
    `${Apis.setupWizardCampaignFormTemplates}?business_type_id=${encodeURIComponent(businessTypeId)}&goals=${encodeURIComponent(goalsParam)}`
  );
  const payload = res.data || {};
  const templates = payload.templates;
  const hasTemplates = Array.isArray(templates);
  return {
    ok: res.status >= 200 && res.status < 300 && hasTemplates && !payload.error,
    data: hasTemplates ? payload : undefined,
    status: res.status,
  };
}

export async function createOrResumeSession() {
  const res = await APIService.post(Apis.setupWizardSessions, {});
  return unwrapPost(res);
}

export async function getSession(sessionId) {
  const res = await APIService.get(Apis.setupWizardSession(sessionId));
  return {
    ok: res.status >= 200 && res.status < 300,
    data: res.data,
    status: res.status,
  };
}

export async function patchSession(sessionId, body) {
  const res = await APIService.patch(Apis.setupWizardSession(sessionId), body);
  return unwrapPost(res);
}

export async function deleteSession(sessionId) {
  const res = await APIService.remove(Apis.setupWizardSession(sessionId));
  return unwrapPost(res);
}

export async function putStepDraft(sessionId, stepKey, body) {
  const res = await APIService.put(Apis.setupWizardStep(sessionId, stepKey), body);
  return unwrapPost(res);
}

export async function resetStepDraft(sessionId, stepKey) {
  return putStepDraft(sessionId, stepKey, { _reset: true });
}

export async function validateStep(sessionId, stepKey) {
  const res = await APIService.post(Apis.setupWizardStepValidate(sessionId, stepKey), {});
  return unwrapPost(res);
}

export async function completeStep(sessionId, stepKey) {
  const res = await APIService.post(Apis.setupWizardStepComplete(sessionId, stepKey), {});
  return unwrapPost(res);
}

export async function skipWizardStep(sessionId, stepKey) {
  return postWizard(Apis.setupWizardStepSkip(sessionId, stepKey), {});
}

export async function goLive(sessionId) {
  const res = await APIService.post(Apis.setupWizardGoLive(sessionId), {});
  return unwrapPost(res);
}

export async function createWizardProfile(sessionId) {
  return postWizard(Apis.setupWizardCreateProfile(sessionId), {});
}

export async function fetchProfileById(profileId) {
  const res = await APIService.get(`${Apis.getProfileList}${profileId}/`);
  return {
    ok: res.status >= 200 && res.status < 300,
    data: res.data,
    status: res.status,
  };
}

export async function fetchProfileList(params = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const search = params.search ?? '';
  let url = `${Apis.getProfileList}?next_page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`;
  if (params.organisation_id) {
    url += `&organisation_id=${encodeURIComponent(params.organisation_id)}`;
  }
  const requestOptions = params.signal ? { signal: params.signal } : {};
  const res = await APIService.get(url, requestOptions);
  return {
    ok: res.status >= 200 && res.status < 300,
    data: res.data,
    status: res.status,
  };
}

export const onboardingService = {
  fetchCampaignFormTemplates,
  createOrResumeSession,
  getSession,
  patchSession,
  deleteSession,
  putStepDraft,
  resetStepDraft,
  validateStep,
  completeStep,
  skipWizardStep,
  goLive,
  createWizardProfile,
  fetchProfileById,
  fetchProfileList,
};

export default onboardingService;
